<template>
  <!-- TODO: Add Terminal to Favorites @action & loading-->
  <base-dialog
    :show="showDialog"
    :show-cancel="$t('common.action.cancel')"
    :action-name="$t('common.action.addAsFavorite')"
    :title="$t('page.dashboard.terminals.addFavoriteTerminal')"
    :action-disabled="!isFormValid"
    :max-width="$vuetify.display.mdAndUp ? '30%' : '100%'"
    @action="addFavoriteTerminal"
    @close="showDialog = false"
    :isLoading="useReaderStore().isLoading"
  >
    <template #content>
      <v-form v-model="isFormValid" class="mt-5">
        <v-row class="mx-2" no-gutters>
          <v-col>
            <v-card-text class="text-center text-body-1 text-grey-darken-3">
              {{ $t("page.dashboard.terminals.selectLocation") }}
            </v-card-text>
          </v-col>
          <v-col>
            <v-select
              :label="$t('page.locations.locations')"
              required
              :rules="selectRules"
              :items="locationOptions"
              item-title="name"
              item-value="id"
              v-model="selectedLocation"
              @update:model-value="locationSelected = true"
            />
          </v-col>
        </v-row>
        <v-row class="mx-2" no-gutters>
          <v-col>
            <v-card-text class="text-center text-body-1 text-grey-darken-3">
              {{ $t("page.dashboard.terminals.selectTerminal") }}
            </v-card-text>
          </v-col>
          <v-col>
            <v-select
              :label="$t('page.terminals.terminals')"
              required
              :rules="selectRules"
              v-model="selectedTerminal"
              :disabled="!locationSelected"
              :items="terminalOptions"
              item-title="label"
              item-value="id"
              density="comfortable"
              variant="outlined"
            />
          </v-col>
        </v-row>
      </v-form>
    </template>
  </base-dialog>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import BaseDialog from "@/shared/components/Common/BaseDialog.vue";
import { useValidation } from "@/shared/composables/validation";
import { useLocationStore } from "@/terminals/stores/locations";
import { useReaderStore } from "@/terminals/stores/readers";
import { useAuthStore } from "@/auth/stores/auth";
import { useAccountStore } from "@/account/stores/account";
import { Emit } from "@/shared/enums/emit.enum";
import type { Reader } from "@/terminals/types/readers";

const selectedLocation = ref<string | null>(null);
const selectedTerminal = ref<string | null>(null);
const locationSelected = ref(false);
const locations = ref(useLocationStore().globalLocations.locations || []);
const readers = ref<Reader[]>([]);
const showDialog = ref(false);
const locationId = ref<string | null>(null);
const userId = useAuthStore().getUserId;
const { isFormValid, selectRules } = useValidation();
const { addFavoriteReader } = useReaderStore();
const emit = defineEmits([Emit.ADDED]);

onMounted(async () => {
  readers.value = (await useReaderStore().listReaders(true, false)) ?? [];
});
const open = async () => {
  showDialog.value = true;
};

defineExpose({
  open,
});

const locationIdFromReaders = computed(() => {
  return readers.value?.flatMap((reader) => reader.location);
});

const locationOptions = computed(() => {
  const options = Array.from(
    new Set(
      locations.value.map((location) => {
        locationId.value = location.id;
        return { id: location.id, name: location.display_name };
      }),
    ),
  );
  return options.filter((aLocation) => locationIdFromReaders.value?.includes(aLocation.id));
});

const terminalOptions = computed(() => {
  const currentAccountId = useAccountStore().account?.id || "";
  const favReaders = useAuthStore().realTimeFavReaders;
  return readers.value
    .filter(
      (reader) =>
        reader.location === selectedLocation.value &&
        !reader.users?.includes(userId) &&
        !(favReaders[currentAccountId]?.includes(reader.id) || false),
    )
    .map((reader) => ({
      label: reader.label,
      id: reader.id,
    }));
});

const addFavoriteTerminal = async () => {
  const terminalId = selectedTerminal.value ?? undefined;
  await addFavoriteReader({ terminalId });
  await useReaderStore().fetchFavReader();
  resetDialog();
  emit(Emit.ADDED);

  showDialog.value = false;
};

const resetDialog = () => {
  selectedLocation.value = null;
  selectedTerminal.value = null;
};
</script>
